/**
 * Header Component
 *
 * Top navigation bar with:
 * - Logo
 * - Environment selector
 * - Admin link (capability-gated)
 * - User menu
 */

import type { Environment } from "@tatvex/types";
import { ChevronDown, User, LogOut, Settings, Monitor, Server, Rocket, Shield } from "lucide-react";
import type { ReactElement } from "react";
import { useState, useCallback, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

import { useAuthStore } from "../../store/authStore";
import { ENVIRONMENT_LABELS } from "../../types";

const ENVIRONMENT_ICONS: Readonly<
  Record<Environment, React.ComponentType<{ className?: string }>>
> = {
  dev: Monitor,
  staging: Server,
  prod: Rocket,
} as const;

export function Header(): ReactElement {
  const { user, environment, setEnvironment, logout, token, hasAnyCapability } = useAuthStore();
  const location = useLocation();

  const isAdminSection = location.pathname.startsWith("/admin");
  const hasAdminAccess = hasAnyCapability([
    "admin:tenants",
    "admin:users",
    "admin:audit",
    "admin:health",
  ]);

  return (
    <header className="flex h-12 items-center justify-between border-b border-border bg-background-secondary px-4">
      {/* Logo */}
      <div className="flex items-center gap-3">
        <Link to="/" className="flex items-center gap-3 hover:opacity-90">
          <Logo />
          <span className="text-lg font-semibold text-foreground">Studio</span>
        </Link>
      </div>

      {/* Right side controls */}
      <div className="flex items-center gap-3">
        {token && (
          <>
            {/* Admin Link */}
            {hasAdminAccess && !isAdminSection && (
              <Link
                to="/admin"
                className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground-muted hover:bg-background-secondary hover:text-foreground transition-colors"
              >
                <Shield className="h-4 w-4" />
                <span>Admin</span>
              </Link>
            )}
            <EnvironmentSelector current={environment} onChange={setEnvironment} />
            <UserMenu user={user} onLogout={logout} />
          </>
        )}
      </div>
    </header>
  );
}

function Logo(): ReactElement {
  return (
    <svg className="h-7 w-7 text-primary" viewBox="0 0 32 32" fill="currentColor">
      <path d="M16 2L2 9v14l14 7 14-7V9L16 2zm0 3.5L26.5 10 16 14.5 5.5 10 16 5.5zM4 11.5l11 5.5v10L4 21.5v-10zm13 15.5V17l11-5.5v10L17 27z" />
    </svg>
  );
}

interface EnvironmentSelectorProps {
  readonly current: Environment;
  readonly onChange: (env: Environment) => void;
}

function EnvironmentSelector({ current, onChange }: EnvironmentSelectorProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const CurrentIcon = ENVIRONMENT_ICONS[current];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    (env: Environment) => {
      onChange(env);
      setIsOpen(false);
    },
    [onChange]
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-sm text-foreground hover:bg-background-secondary"
      >
        <CurrentIcon className="h-4 w-4" />
        <span>{ENVIRONMENT_LABELS[current]}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-md border border-border bg-background-secondary py-1 shadow-lg animate-fade-in">
          {(["dev", "staging", "prod"] as const).map((env) => {
            const Icon = ENVIRONMENT_ICONS[env];
            const isSelected = env === current;

            return (
              <button
                key={env}
                onClick={() => handleSelect(env)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-sm ${
                  isSelected ? "bg-primary/10 text-primary" : "text-foreground hover:bg-background"
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{ENVIRONMENT_LABELS[env]}</span>
                {isSelected && <span className="ml-auto text-xs text-primary">Active</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface UserMenuProps {
  readonly user: { readonly name: string; readonly email: string } | undefined;
  readonly onLogout: () => void;
}

function UserMenu({ user, onLogout }: UserMenuProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!user) {
    return (
      <button className="btn btn-ghost">
        <User className="h-4 w-4" />
        <span className="ml-2">Sign In</span>
      </button>
    );
  }

  const initials = user.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground hover:bg-primary/90"
        title={user.name}
      >
        {initials}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-md border border-border bg-background-secondary py-1 shadow-lg animate-fade-in">
          <div className="border-b border-border px-3 py-2">
            <p className="text-sm font-medium text-foreground">{user.name}</p>
            <p className="text-xs text-foreground-muted">{user.email}</p>
          </div>

          <button className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-background">
            <Settings className="h-4 w-4" />
            <span>Settings</span>
          </button>

          <button
            onClick={() => {
              onLogout();
              setIsOpen(false);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-error hover:bg-background"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign Out</span>
          </button>
        </div>
      )}
    </div>
  );
}
